import { useCallback, useMemo, useState, useEffect } from 'react';
import { formatDateWithOptions } from '@/utils/Date';

export interface SeasonalDates {
  spring?: { start_date?: string; end_date?: string };
  summer?: { start_date?: string; end_date?: string };
  autumn?: { start_date?: string; end_date?: string };
  winter?: { start_date?: string; end_date?: string };
}

type SeasonErrorMap = Partial<Record<keyof SeasonalDates, string>>;

interface SeasonalDatePickerProps {
  seasonalDates: SeasonalDates;
  onChange: (seasonalDates: SeasonalDates) => void;
  disabled?: boolean;
  errors?: { [key: string]: string };
}

export const SeasonalDatePicker = ({
  seasonalDates,
  onChange,
  disabled = false,
  errors = {},
}: SeasonalDatePickerProps) => {
  const CURRENT_YEAR_NUM = new Date().getFullYear();
  const [selectedYear, setSelectedYear] = useState<number>(() => {
    const seasons: (keyof SeasonalDates)[] = [
      'spring',
      'summer',
      'autumn',
      'winter',
    ];
    for (const s of seasons) {
      const sd = seasonalDates[s];
      const candidate = sd?.start_date || sd?.end_date;
      if (candidate && /^\d{4}(-\d{2}){1,2}$/.test(candidate)) {
        const y = parseInt(candidate.split('-')[0], 10);
        if (!isNaN(y)) return y;
      }
    }
    return CURRENT_YEAR_NUM;
  });

  const yearOptions = useMemo(() => {
    const start = CURRENT_YEAR_NUM - 1;
    return Array.from({ length: 6 }, (_, i) => start + i);
  }, [CURRENT_YEAR_NUM]);

  // Helper: safely parse YYYY-MM-DD into a Date. Returns null if invalid or missing day
  const toDate = useCallback((dateStr?: string): Date | null => {
    if (!dateStr) return null;
    const parts = dateStr.split('-');
    if (parts.length < 3 || !parts[2]) return null;
    const [y, m, d] = parts;
    const year = parseInt(y, 10);
    const month = parseInt(m, 10) - 1;
    const day = parseInt(d, 10);
    if (!year || !m || !d) return null;
    const dt = new Date(year, month, day);
    return isNaN(dt.getTime()) ? null : dt;
  }, []);

  // Compute overlap messages per season for a provided dates object
  const computeOverlapMessages = useCallback(
    (dates: SeasonalDates): SeasonErrorMap => {
      const seasons: (keyof SeasonalDates)[] = [
        'spring',
        'summer',
        'autumn',
        'winter',
      ];
      const labels: Record<keyof SeasonalDates, string> = {
        spring: 'Spring',
        summer: 'Summer',
        autumn: 'Autumn',
        winter: 'Winter',
      };
      const ranges: Partial<
        Record<keyof SeasonalDates, { start: Date; end: Date }>
      > = {};
      seasons.forEach((s) => {
        const sd = dates[s];
        if (!sd?.start_date || !sd?.end_date) return;
        const start = toDate(sd.start_date);
        const end = toDate(sd.end_date);
        if (!start || !end) return;
        if (end.getTime() < start.getTime()) return;
        ranges[s] = { start, end };
      });

      const overlapsWith: Partial<Record<keyof SeasonalDates, string[]>> = {};
      for (let i = 0; i < seasons.length; i++) {
        for (let j = i + 1; j < seasons.length; j++) {
          const a = seasons[i];
          const b = seasons[j];
          const ra = ranges[a];
          const rb = ranges[b];
          if (!ra || !rb) continue;
          const intersects =
            ra.start.getTime() <= rb.end.getTime() &&
            rb.start.getTime() <= ra.end.getTime();
          if (intersects) {
            overlapsWith[a] = [...(overlapsWith[a] || []), labels[b]];
            overlapsWith[b] = [...(overlapsWith[b] || []), labels[a]];
          }
        }
      }

      const messages: SeasonErrorMap = {};
      seasons.forEach((s) => {
        if (overlapsWith[s]?.length) {
          const list = overlapsWith[s]!.join(', ');
          messages[s] = `Season dates overlap with: ${list}.`;
        }
      });
      return messages;
    },
    [toDate]
  );

  const overlapMessages = useMemo(
    () => computeOverlapMessages(seasonalDates),
    [seasonalDates, computeOverlapMessages]
  );

  // Store last blocked overlap attempt messages to show immediate feedback
  const [blockedOverlap, setBlockedOverlap] = useState<SeasonErrorMap>({});

  // Helper: get available days for a given month (1-12), using correct month length
  const getAvailableDays = useCallback(
    (season: string, month: string, year?: number): number[] => {
      const monthNum = parseInt(month, 10);
      if (!monthNum || monthNum < 1 || monthNum > 12) return [];
      const y = year || new Date().getFullYear();
      const daysInMonth = new Date(y, monthNum, 0).getDate();
      return Array.from({ length: daysInMonth }, (_, i) => i + 1);
    },
    []
  );

  // Parse date string to get month, day, year
  const parseDate = useCallback(
    (dateStr: string): { month: string; day: string; year: string } => {
      if (!dateStr) return { month: '', day: '', year: String(selectedYear) };
      const [year, month, day] = dateStr.split('-');
      return {
        month: month || '',
        day: day || '',
        year: year || String(selectedYear),
      };
    },
    [selectedYear]
  );

  // Format date from month, day, year
  const formatDate = useCallback(
    (month: string, day: string, yearOverride?: string): string => {
      if (!month) return ''; // Return empty only if no month
      const paddedMonth = month.padStart(2, '0');
      const paddedDay = day ? day.padStart(2, '0') : '';
      const year = yearOverride || String(selectedYear);
      return paddedDay
        ? `${year}-${paddedMonth}-${paddedDay}`
        : `${year}-${paddedMonth}`;
    },
    [selectedYear]
  );

  // Handle date changes
  const handleDateChange = useCallback(
    (
      season: keyof SeasonalDates,
      dateType: 'start_date' | 'end_date',
      field: 'month' | 'day',
      value: string
    ) => {
      // Get current values
      const currentDate = seasonalDates[season]?.[dateType] || '';
      const { month, day } = parseDate(currentDate);

      // Determine new values based on what field is being updated and ensure proper padding
      const updatedMonth = field === 'month' ? value.padStart(2, '0') : month;
      const updatedDay =
        field === 'day' ? value.padStart(2, '0') : field === 'month' ? '' : day;

      // Always format the date when we have a month
      const formattedDate = formatDate(
        updatedMonth,
        updatedDay,
        String(selectedYear)
      );

      // Update the state with the new date, preserving the existing structure
      const updatedSeasonalDates = {
        ...seasonalDates,
        [season]: {
          ...seasonalDates[season],
          [dateType]: formattedDate,
        },
      };

      // Prevent setting values that would introduce overlaps
      const potentialOverlap = computeOverlapMessages(updatedSeasonalDates);
      const hasAnyOverlap = Object.keys(potentialOverlap).length > 0;
      if (hasAnyOverlap) {
        setBlockedOverlap(potentialOverlap);
        return; // do not apply invalid change
      }

      // Clear any blocked messages and apply change
      if (Object.keys(blockedOverlap).length) setBlockedOverlap({});
      onChange(updatedSeasonalDates);
    },
    [
      seasonalDates,
      onChange,
      parseDate,
      formatDate,
      selectedYear,
      computeOverlapMessages,
      blockedOverlap,
      setBlockedOverlap,
    ]
  );

  // Get months list (unlock all months 1-12 for all seasons)
  const getSeasonMonths = useCallback((): number[] => {
    return Array.from({ length: 12 }, (_, i) => i + 1);
  }, []);

  // When the selected year changes, keep any chosen months/days but update the year part
  useEffect(() => {
    const seasons: (keyof SeasonalDates)[] = [
      'spring',
      'summer',
      'autumn',
      'winter',
    ];
    let changed = false;
    const updated: SeasonalDates = {};

    seasons.forEach((s) => {
      const sd = seasonalDates[s];
      if (!sd) return;
      const updatePart = (val?: string) => {
        if (!val) return val;
        const parts = val.split('-');
        if (!parts[0]) return val;
        const newVal = [String(selectedYear), parts[1], parts[2]]
          .filter(Boolean)
          .join('-');
        if (newVal !== val) changed = true;
        return newVal;
      };
      const nextStart = updatePart(sd.start_date);
      const nextEnd = updatePart(sd.end_date);
      updated[s] = {
        start_date: nextStart,
        end_date: nextEnd,
      };
    });

    if (changed) {
      onChange({ ...seasonalDates, ...updated });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedYear]);

  // Render date picker for a specific date (start or end)
  const renderDatePicker = (
    season: keyof SeasonalDates,
    dateType: 'start_date' | 'end_date',
    label: string,
    borderColorClass: string
  ) => {
    const dateStr = seasonalDates[season]?.[dateType] || '';
    const { month, day, year } = parseDate(dateStr);
    const months = getSeasonMonths();
    const days = month ? getAvailableDays(season, month, parseInt(year)) : [];

    // Generate unique IDs for the select elements
    const monthId = `${season}-${dateType}-month`;
    const dayId = `${season}-${dateType}-day`;

    return (
      <div key={`${season}-${dateType}`}>
        <label
          htmlFor={monthId}
          className={`block text-xs font-medium ${borderColorClass} mb-1`}
        >
          {label}
        </label>
        <div className="flex gap-2">
          {/* Month */}
          <select
            id={monthId}
            name={`${season}_${dateType}_month`}
            className={`flex-1 h-8 px-2 py-1 text-sm border ${borderColorClass.replace('text-', 'border-')} rounded-md focus:ring-2 focus:ring-${borderColorClass.split('-')[1]}-500 focus:border-${borderColorClass.split('-')[1]}-500 bg-gray-100`}
            value={month ? month.padStart(2, '0') : ''}
            onChange={(e) => {
              const selectedMonth = e.target.value;
              handleDateChange(season, dateType, 'month', selectedMonth);
            }}
            disabled={disabled}
            aria-label={`${label} month`}
          >
            <option value="">Month</option>
            {months.map((m) => (
              <option key={m} value={m.toString().padStart(2, '0')}>
                {formatDateWithOptions(new Date(0, m - 1), {
                  month: 'short',
                }, 'en')}
              </option>
            ))}
          </select>

          {/* Day */}
          <select
            id={dayId}
            name={`${season}_${dateType}_day`}
            className={`flex-1 h-8 px-2 py-1 text-sm border ${borderColorClass.replace('text-', 'border-')} rounded-md focus:ring-2 focus:ring-${borderColorClass.split('-')[1]}-500 focus:border-${borderColorClass.split('-')[1]}-500 bg-gray-100`}
            value={day ? day.padStart(2, '0') : ''}
            onChange={(e) =>
              handleDateChange(season, dateType, 'day', e.target.value)
            }
            disabled={disabled || !month}
            aria-label={`${label} day`}
          >
            <option value="">Day</option>
            {days.map((d) => (
              <option key={d} value={String(d).padStart(2, '0')}>
                {d}
              </option>
            ))}
          </select>
        </div>
        {(() => {
          const shortKey = `${season}_${dateType === 'start_date' ? 'start' : 'end'}`;
          const longKey = `${season}_${dateType}`; // e.g., spring_start_date
          const msg = errors[shortKey] || errors[longKey];
          return msg ? (
            <p className="text-red-500 text-xs mt-1">{msg}</p>
          ) : null;
        })()}
      </div>
    );
  };

  // Render season section
  const renderSeasonSection = (
    season: keyof SeasonalDates,
    title: string,
    gradientClass: string,
    iconBgClass: string,
    textColorClass: string,
    borderColorClass: string,
    icon: JSX.Element
  ) => {
    return (
      <div
        key={season}
        className={`${gradientClass} p-4 rounded-lg border ${borderColorClass}`}
      >
        <div className="flex items-center gap-2 mb-3">
          <div className={`p-1.5 ${iconBgClass} rounded-full`}>{icon}</div>
          <span className={`font-semibold ${textColorClass}`}>{title}</span>
        </div>
        <div className="space-y-2">
          {renderDatePicker(season, 'start_date', 'Start Date', textColorClass)}
          {renderDatePicker(season, 'end_date', 'End Date', textColorClass)}
        </div>
        {errors[`seasonal_dates.${season}`] && (
          <p className="text-red-500 text-sm mt-1">
            {errors[`seasonal_dates.${season}`]}
          </p>
        )}
        {(blockedOverlap[season] || overlapMessages[season]) && (
          <p className="text-red-500 text-sm mt-1">
            {blockedOverlap[season] || overlapMessages[season]}
          </p>
        )}
      </div>
    );
  };

  return (
    <div className="bg-gray-200 rounded-lg border border-gray-200 p-4">
      {/* Year selector */}
      <div className="mb-4">
        <label
          htmlFor="seasonal-year-selector"
          className="block text-sm font-medium text-gray-700 mb-2"
        >
          For which year you are selecting the Seasonal
        </label>
        <select
          id="seasonal-year-selector"
          className="select select-bordered w-full max-w-xs"
          value={selectedYear}
          onChange={(e) => {
            const newYear = parseInt(e.target.value, 10);
            if (isNaN(newYear)) return;
            setSelectedYear(newYear);
          }}
          disabled={disabled}
          aria-label="Select year for seasonal dates"
        >
          {yearOptions.map((y) => (
            <option key={y} value={y}>
              {y}
            </option>
          ))}
        </select>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-4">
        {/* Spring */}
        {renderSeasonSection(
          'spring',
          'Spring',
          'bg-gradient-to-br from-green-50 dark:from-green-950 contrast:from-green-950 to-green-100 dark:to-green-950 contrast:to-green-950',
          'bg-green-500 dark:bg-green-950 contrast:bg-green-950',
          'text-green-700 dark:text-green-200 contrast:text-green-200',
          'border-green-200 dark:border-green-950 contrast:border-green-950',
          <svg
            className="w-4 h-4 text-white"
            fill="currentColor"
            viewBox="0 0 20 20"
          >
            <path
              fillRule="evenodd"
              d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-8.293l-3-3a1 1 0 00-1.414 0l-3 3a1 1 0 001.414 1.414L9 9.414V13a1 1 0 102 0V9.414l1.293 1.293a1 1 0 001.414-1.414z"
              clipRule="evenodd"
            />
          </svg>
        )}

        {/* Summer */}
        {renderSeasonSection(
          'summer',
          'Summer',
          'bg-gradient-to-br from-yellow-50 dark:from-yellow-950 contrast:from-yellow-950 to-orange-100 dark:to-orange-950 contrast:to-orange-950',
          'bg-yellow-500 dark:bg-yellow-950 contrast:bg-yellow-950',
          'text-yellow-700 dark:text-yellow-200 contrast:text-yellow-200',
          'border-yellow-200 dark:border-yellow-950 contrast:border-yellow-950',
          <svg
            className="w-4 h-4 text-white"
            fill="currentColor"
            viewBox="0 0 20 20"
          >
            <path
              fillRule="evenodd"
              d="M10 2L13.09 8.26L20 9L15 13.74L16.18 20L10 16.9L3.82 20L5 13.74L0 9L6.91 8.26L10 2Z"
              clipRule="evenodd"
            />
          </svg>
        )}

        {/* Autumn */}
        {renderSeasonSection(
          'autumn',
          'Autumn',
          'bg-gradient-to-br from-orange-50 dark:from-orange-950 contrast:from-orange-950 to-red-100 dark:to-red-950 contrast:to-red-950',
          'bg-orange-500 dark:bg-orange-950 contrast:bg-orange-950',
          'text-orange-700 dark:text-orange-200 contrast:text-orange-200',
          'border-orange-200 dark:border-orange-950 contrast:border-orange-950',
          <svg
            className="w-4 h-4 text-white"
            fill="currentColor"
            viewBox="0 0 20 20"
          >
            <path
              fillRule="evenodd"
              d="M3 4a1 1 0 011-1h12a1 1 0 011 1v2a1 1 0 01-1 1H4a1 1 0 01-1-1V4zM3 10a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H4a1 1 0 01-1-1v-6zM14 9a1 1 0 00-1 1v6a1 1 0 001 1h2a1 1 0 001-1v-6a1 1 0 00-1-1h-2z"
              clipRule="evenodd"
            />
          </svg>
        )}

        {/* Winter */}
        {renderSeasonSection(
          'winter',
          'Winter',
          'bg-gradient-to-br from-blue-50 dark:from-blue-950 contrast:from-blue-950 to-cyan-100 dark:to-indigo-950 contrast:to-cyan-950',
          'bg-blue-500 dark:bg-blue-950 contrast:bg-blue-950',
          'text-blue-700 dark:text-blue-200 contrast:text-blue-200',
          'border-blue-200 dark:border-blue-950 contrast:border-blue-950',
          <svg
            className="w-4 h-4 text-white"
            fill="currentColor"
            viewBox="0 0 20 20"
          >
            <path
              fillRule="evenodd"
              d="M10 2a1 1 0 011 1v1.323l3.954 1.582 1.599-.8a1 1 0 01.894 1.79l-1.233.616 1.738 5.42a1 1 0 01-.285 1.05A3.989 3.989 0 0115 15a3.989 3.989 0 01-2.667-1.019 1 1 0 01-.285-1.05l1.715-5.349L11 6.477V16h2a1 1 0 110 2H7a1 1 0 110-2h2V6.477L6.237 7.582l1.715 5.349a1 1 0 01-.285 1.05A3.989 3.989 0 015 15a3.989 3.989 0 01-2.667-1.019 1 1 0 01-.285-1.05l1.738-5.42-1.233-.617a1 1 0 01.894-1.788l1.599.799L9 4.323V3a1 1 0 011-1z"
              clipRule="evenodd"
            />
          </svg>
        )}
      </div>
    </div>
  );
};

export default SeasonalDatePicker;
